import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PermissionContext } from 'context/Context';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { FaPlaneDeparture, FaPlus, FaCheck, FaBan, FaChartBar, FaRegCalendarAlt, FaSearch, FaTimes } from 'react-icons/fa';
import { MdListAlt, MdFiberNew, MdPending, MdCancel, MdFilterAlt } from 'react-icons/md';
import { Card, Tabs, Button, DateRangePicker, CheckboxMultiSelect, AdvanceFilterDrawer, getEmptyValues } from 'components/common';
import TravelBookingGrid from 'components/app/bookings/travel/booking/component/TravelBookingGrid';
import { debounce } from 'helpers/debounce';
import { TRAVEL_TYPE_OF_TRAVEL, TRAVEL_PURPOSE_OF_TRAVEL } from 'app_config';
import { activeEmployeesDebounce, activeEmployeesByNameDebounce } from 'helpers/employeeOptionHelper';
import {
  getAllBookings,
  getSumitedBookings,
  getWaitingConfirmationBookings,
  getWaitingVerificationBookings,
  getWaitingApprovalBookings,
  getApprovedBookings,
  getRejectedBookings,
  getCanceledBookings,
  getAllStatuses,
} from 'redux/booking/travel/travelBookingSlice';
import { getActiveSites } from 'redux/organization/siteSlice';

const SEARCH_KEY = 'id_or_trip_name_or_destination_or_job_number_cont';
const thisYear = () => ({ startDate: dayjs().startOf('year').format('YYYY-MM-DD'), endDate: dayjs().endOf('year').format('YYYY-MM-DD') });
// travel_statuses.id → dot color
const STATUS_COLOR = {
  1: '#2563eb', 2: '#f59e0b', 3: '#22c55e', 4: '#ef4444', 5: '#94a3b8',
  6: '#f59e0b', 7: '#06b6d4', 8: '#f59e0b', 10: '#f59e0b', 11: '#06b6d4',
};

const TravelBookingPage = () => {
  const navigate = useNavigate();
  const { permissions = {} } = useContext(PermissionContext);
  const canDashboard = permissions.v515 === true;
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getAllStatuses());
    dispatch(getActiveSites());
  }, [dispatch]);

  const bookingTravel = useSelector((s) => s.booking_travel);
  const bookingStatuses = bookingTravel.bookingStatuses;
  const siteData = useSelector((s) => s.organization_site.activeData);

  const statusOptions = useMemo(() => (bookingStatuses || []).map((i) => ({ value: i.id, label: i.name, color: STATUS_COLOR[i.id] || '#94a3b8' })), [bookingStatuses]);
  const siteOptions = useMemo(() => (siteData || []).map((i) => ({ value: i.id, label: i.name })), [siteData]);
  const typeOptions = useMemo(() => TRAVEL_TYPE_OF_TRAVEL.map((t) => ({ value: t, label: t })), []);
  const purposeOptions = useMemo(() => TRAVEL_PURPOSE_OF_TRAVEL.map((p) => ({ value: p, label: p })), []);

  // Advanced-filter fields — secondary filters live here to keep the quick bar clean.
  const advancedFields = useMemo(() => [
    { key: 'id', label: 'Booking number', type: 'text', paramKey: 'id_cont', tagName: 'ID#' },
    { key: 'trip_name', label: 'Trip name', type: 'text', paramKey: 'trip_name_cont', tagName: 'Trip name' },
    { key: 'destination', label: 'Destination', type: 'text', paramKey: 'destination_cont', tagName: 'Destination' },
    { key: 'job_number', label: 'Job number', type: 'text', paramKey: 'job_number_cont', tagName: 'Job number' },
    { key: 'type_of_travel', label: 'Type of trip', type: 'multiselect', options: typeOptions, paramKey: 'type_of_travel_in', tagName: 'Type' },
    { key: 'purpose_of_travel', label: 'Purpose of travel', type: 'multiselect', options: purposeOptions, paramKey: 'purpose_of_travel_in', tagName: 'Purpose' },
    { key: 'employees', label: 'Employees traveler', type: 'async_multiselect', loadOptions: activeEmployeesDebounce, paramKey: 'bookings_employees_employee_id_in', tagName: 'Employee' },
    { key: 'created_date', label: 'Created date', type: 'date_range', paramKeys: ['created_at_gteq', 'created_at_lteq'], tagName: 'Created date' },
    { key: 'created_by', label: 'Created by', type: 'async_multiselect', loadOptions: activeEmployeesByNameDebounce, paramKey: 'user_name_in', tagName: 'Created by' },
    { key: 'approved_by', label: 'Approved by', type: 'async_multiselect', loadOptions: activeEmployeesByNameDebounce, paramKey: 'approved_by_in', tagName: 'Approved by' },
    { key: 'approved_date', label: 'Approved date', type: 'date_range', paramKeys: ['approved_date_gteq', 'approved_date_lteq'], tagName: 'Approved date' },
    { key: 'follow_project_budget', label: 'Follow project budget', type: 'checkbox', paramKey: 'follow_project_budget_true', tagName: 'Follow budget' },
  ], [typeOptions, purposeOptions]);

  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [travelDate, setTravelDate] = useState(thisYear());
  const [statusIds, setStatusIds] = useState([]);
  const [siteIds, setSiteIds] = useState([]);

  const [showFilter, setShowFilter] = useState(false);
  const [filterValues, setFilterValues] = useState({});
  const [advancedParams, setAdvancedParams] = useState({});

  const applySearch = useRef(debounce((v) => setSearch(v), 500)).current;
  const advancedCount = Object.keys(advancedParams).length;

  const extraParams = useMemo(() => {
    const p = { ...advancedParams };
    if (search) p[SEARCH_KEY] = search;
    if (travelDate.startDate) { p.date_of_travel_gteq = travelDate.startDate; p.date_of_travel_lteq = travelDate.endDate; }
    if (statusIds.length) p.status_id_in = statusIds;
    if (siteIds.length) p.site_id_in = siteIds;
    return p;
  }, [search, travelDate, statusIds, siteIds, advancedParams]);

  const badge = (key) => { const t = bookingTravel[key]?.totalRows; return t ? t.toLocaleString() : undefined; };
  const grid = (stateKey, loadThunk, exportStatus) => (
    <TravelBookingGrid stateKey={stateKey} loadThunk={loadThunk} exportStatus={exportStatus} extraParams={extraParams} />
  );

  const tabs = [
    { label: 'All', icon: <MdListAlt size={14} />, badge: badge('allData'), badgeColor: '#6366f1', content: grid('allData', getAllBookings, 'all') },
    { label: 'New Submission', icon: <MdFiberNew size={15} />, badge: badge('submitedData'), badgeColor: '#2563eb', content: grid('submitedData', getSumitedBookings, 'submited') },
    { label: 'W. confirmation', icon: <MdPending size={14} />, badge: badge('waitingConfirmationData'), badgeColor: '#f59e0b', content: grid('waitingConfirmationData', getWaitingConfirmationBookings, 'waiting_confirmation') },
    { label: 'W. verification', icon: <MdPending size={14} />, badge: badge('waitingVerificationData'), badgeColor: '#f59e0b', content: grid('waitingVerificationData', getWaitingVerificationBookings, 'waiting_verification') },
    { label: 'W. approval', icon: <MdPending size={14} />, badge: badge('waitingApprovalData'), badgeColor: '#f59e0b', content: grid('waitingApprovalData', getWaitingApprovalBookings, 'waiting_approval') },
    { label: 'Approved', icon: <FaCheck size={13} />, badge: badge('approvedData'), badgeColor: '#22c55e', content: grid('approvedData', getApprovedBookings, 'approved') },
    { label: 'Rejected', icon: <FaBan size={13} />, badge: badge('rejectedData'), badgeColor: '#ef4444', content: grid('rejectedData', getRejectedBookings, 'rejected') },
    { label: 'Canceled', icon: <MdCancel size={14} />, badge: badge('canceledData'), badgeColor: '#94a3b8', content: grid('canceledData', getCanceledBookings, 'canceled') },
  ];

  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4">
      <div className="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-3">
        <div className="tw-flex tw-items-center tw-gap-3">
          <div className="tw-w-1.5 tw-h-10 tw-rounded-sm tw-flex-shrink-0" style={{ background: 'linear-gradient(to bottom, #0ea5e9, #6366f1)' }} />
          <div className="tw-flex tw-items-center tw-gap-2">
            <FaPlaneDeparture className="tw-text-blue-600" size={20} />
            <div>
              <h1 className="tw-mb-0 tw-font-bold tw-text-2xl tw-text-gray-900">Travel Bookings</h1>
              <p className="tw-text-gray-500 tw-text-sm tw-mb-0">Manage travel booking requests across all statuses.</p>
            </div>
          </div>
        </div>
        {/* wrap: three buttons do not fit one phone row, and the outer
            flex-wrap only moves the group as a whole, not its contents. */}
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          {canDashboard && (
            <Button variant="secondary" size="sm" leftIcon={FaChartBar} onClick={() => navigate('/dashboard/booking/travel')}>Dashboard</Button>
          )}
          <Button variant="secondary" size="sm" leftIcon={FaRegCalendarAlt} onClick={() => navigate('/booking/travel/calendar')}>Calendar</Button>
          <Button variant="primary" size="sm" leftIcon={FaPlus} onClick={() => navigate('/booking/travel/new-travel-request')}>New Travel booking</Button>
        </div>
      </div>

      {/* Compact quick filters (secondary filters moved to More Filters) */}
      <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-slate-200">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-px-3 tw-py-2.5">
          <div className="tw-relative tw-flex tw-items-center tw-w-full sm:tw-w-56 sm:tw-flex-shrink-0">
            <FaSearch className="tw-absolute tw-left-3 tw-text-slate-400 tw-pointer-events-none" size={12} />
            <input type="text" value={searchText} onChange={(e) => { setSearchText(e.target.value); applySearch(e.target.value); }} placeholder="ID, trip, destination, job..."
              className="tw-w-full tw-pl-8 tw-pr-7 tw-py-1.5 tw-text-sm tw-border tw-border-slate-200 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-400 focus:tw-border-transparent tw-bg-slate-50 hover:tw-bg-white tw-transition-colors tw-text-slate-700 tw-placeholder-slate-400" />
            {searchText && <button type="button" onClick={() => { setSearchText(''); setSearch(''); }} className="tw-absolute tw-right-2 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-xl tw-bg-slate-300 hover:tw-bg-slate-400 tw-text-white tw-border-0 tw-cursor-pointer tw-p-0"><FaTimes size={8} /></button>}
          </div>
          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />
          <DateRangePicker label="Travel date" startDate={travelDate.startDate} endDate={travelDate.endDate} onChange={setTravelDate} />
          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />
          <CheckboxMultiSelect options={statusOptions} value={statusIds} onChange={setStatusIds} allLabel="All statuses" />
          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />
          <CheckboxMultiSelect options={siteOptions} value={siteIds} onChange={setSiteIds} allLabel="All sites" />
          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />
          <button
            type="button"
            onClick={() => setShowFilter(true)}
            className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded tw-border tw-text-sm tw-font-medium tw-cursor-pointer tw-transition-all tw-flex-shrink-0 ${advancedCount > 0 ? 'tw-bg-primary-600 tw-border-primary-600 tw-text-white hover:tw-bg-primary-700' : 'tw-bg-white tw-border-slate-200 tw-text-slate-600 hover:tw-border-primary-300 hover:tw-text-primary-600 hover:tw-bg-primary-50'}`}
          >
            <MdFilterAlt size={15} /> More Filters
            {advancedCount > 0 && <span className="tw-inline-flex tw-items-center tw-justify-center tw-min-w-[18px] tw-h-[18px] tw-px-1 tw-rounded-xl tw-bg-white/30 tw-text-white tw-text-xs tw-font-bold tw-leading-none">{advancedCount}</span>}
          </button>
        </div>
      </div>

      <Card padding="none">
        <Tabs variant="underline" lazyMount navClassName="tw-px-3 tw-pt-2 tw-bg-white tw-flex-nowrap tw-overflow-x-auto tw-no-scrollbar" contentClassName="tw-bg-slate-50 tw-p-4" tabs={tabs} />
      </Card>

      <AdvanceFilterDrawer
        show={showFilter}
        onClose={() => setShowFilter(false)}
        onApply={(params) => { setAdvancedParams(params); setShowFilter(false); }}
        onReset={() => { setAdvancedParams({}); setFilterValues(getEmptyValues(advancedFields)); }}
        fields={advancedFields}
        values={filterValues}
        onChange={(key, val) => setFilterValues((prev) => ({ ...prev, [key]: val }))}
        title="More filters — travel bookings"
      />
    </div>
  );
};

export default TravelBookingPage;
